import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import type { ProductSize } from '@velasca/types'
import { ConditionalLink } from '@velasca/ui/conditional-link'
import { HorizontalCardDescription } from '@velasca/ui/horizontal-card-description'
import { HorizontalCardPrice } from '@velasca/ui/horizontal-card-price'
import { HorizontalCardTitle } from '@velasca/ui/horizontal-card-title'
import { MobilePopup } from '@velasca/ui/mobile-popup'
import { PriceRow } from '@velasca/ui/price-row'
import { SansText } from '@velasca/ui/sans-text'
import { buildIntrinsicWidthSources } from '@velasca/utils/build-intrinsic-width-sources'
import { getSizeGuideUrl } from '@velasca/utils/get-size-guide-url'
import { setQueryParams } from '@velasca/utils/set-query-params'
import { QuickAddSizeSelectorLineWithIcons } from './components/QuickAddSizeSelectorLineWithIcons'
import { QuickAddSkeleton } from './components/QuickAddSkeleton'
import {
  Details,
  Header,
  Images,
  Product,
  ProductImage,
  ProductTitleLink,
  Spacer,
  StyledQuickAddList,
} from './QuickAdd.styles'
import type { QuickAddProps } from './QuickAdd.types'

export const QuickAdd = ({
  className,
  loading = false,
  title,
  price,
  compareAtPrice,
  isPriceDiscounted,
  productType,
  description,
  isGiftCard,
  sizes,
  image,
  handle,
  isOpen,
  onClose,
  onAddToCart,
  shouldOpenCartDrawerOnAddToCart,
}: QuickAddProps): JSX.Element | null => {
  const { t } = useTranslation('product')

  const sizeGuideUrl = useMemo(() => getSizeGuideUrl(handle), [handle])

  return (
    <MobilePopup isVisible={isOpen} setIsVisible={onClose}>
      {loading ? (
        <QuickAddSkeleton />
      ) : (
        <Product className={className}>
          <Header>
            <Images>
              {/* {staffPickIcon?.image?.src ? (
                <StaffPickImage
                  alt={staffPickIcon?.image?.alt ?? ''}
                  imgSrc={setQueryParams(staffPickIcon?.image?.src, [
                    { name: 'width', value: '100' },
                  ])}
                  sources={buildDprSources(staffPickIcon?.image?.src, [
                    { width: 100, dpr: 1 },
                    { width: 200, dpr: 2 },
                    { width: 300, dpr: 3 },
                  ])}
                />
              ) : null} */}
              {image?.src && (
                <a href={`/products/${handle ?? ''}`}>
                  <ProductImage
                    lazyload
                    lqip
                    showDefault
                    alt={image?.alt ?? ''}
                    draggable={false}
                    transitionDuration={50}
                    imgSrc={setQueryParams(image?.src, [
                      { name: 'width', value: '30' },
                    ])}
                    sources={buildIntrinsicWidthSources({
                      src: image.src,
                      maxWidth: image.width,
                    })}
                  />
                </a>
              )}
            </Images>

            <Details>
              <div>
                <HorizontalCardDescription className="product-type">
                  {isGiftCard ? '' : productType}
                </HorizontalCardDescription>
                <ConditionalLink
                  CustomLink={ProductTitleLink}
                  condition={Boolean(handle)}
                  href={`/products/${handle}`}
                >
                  <HorizontalCardTitle>{title}</HorizontalCardTitle>
                </ConditionalLink>

                {!!description && (
                  <Spacer>
                    {Boolean(description.thin) && (
                      <HorizontalCardDescription>
                        {description.thin}
                      </HorizontalCardDescription>
                    )}
                    {Boolean(description.bold) && (
                      <HorizontalCardDescription>
                        {description.bold}
                      </HorizontalCardDescription>
                    )}
                  </Spacer>
                )}
                <PriceRow
                  style={{
                    justifyContent: 'start',
                  }}
                >
                  {isPriceDiscounted && compareAtPrice && (
                    <HorizontalCardPrice
                      strikethrough
                      color="gray"
                      dangerouslySetInnerHTML={{
                        __html: compareAtPrice,
                      }}
                    />
                  )}
                  {price && (
                    <HorizontalCardPrice
                      fontWeight="500"
                      dangerouslySetInnerHTML={{
                        __html: price,
                      }}
                    />
                  )}
                </PriceRow>
              </div>
              {sizes && sizes.length > 1 && sizeGuideUrl && (
                <SansText
                  fontWeight="500"
                  fontStyle="italic"
                  as="a"
                  color="primary"
                  href={sizeGuideUrl}
                >
                  {t('sizeLink')}
                </SansText>
              )}
            </Details>
          </Header>

          <StyledQuickAddList>
            {sizes?.map(({ name, quantityAvailable, id }: ProductSize) => {
              return (
                <QuickAddSizeSelectorLineWithIcons
                  key={id}
                  id={id}
                  handle={handle}
                  quantityAvailable={quantityAvailable}
                  name={name}
                  shouldOpenCartDrawerOnAddToCart={
                    shouldOpenCartDrawerOnAddToCart
                  }
                  onClose={onClose}
                  onAddToCart={onAddToCart}
                />
              )
            })}
          </StyledQuickAddList>
        </Product>
      )}
    </MobilePopup>
  )
}
